import { GroupedOptions, SelectOption, SingleOption, isSingleOption } from '@frontend/basic-forms';
import { useLocale } from '@frontend/lang';
import { useAccountRepository } from '@frontend/repository';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { mapResultSetToMappedOptions, mapResultSetToOptions, onQueryParamChange, usePrevious } from '../object-select-input/object-select-input.controller';
import { MultiObjectSelectInputProps } from './multi-object-select-input.component';

interface ViewProps<T extends { id: string }> {
    options: SelectOption<T>[];
    value?: T[];
    onScrollToBottom: () => void;
    search: (value: string) => void;
    onSelect: (option: SingleOption<T>[] | null) => void;
    overwriteDisabled?: boolean;
    hide: boolean;
}

const pageSize = '100';

const useMultiObjectSelectInput = <T extends { id: string }, D extends string | number>(props: MultiObjectSelectInputProps<T, D>): ViewProps<T> => {
    const { locale } = useLocale();
    const { currentAccount } = useAccountRepository();
    const [urlOverwrite, setUrlOverwrite] = useState<T | null>(null);
    const [cache, setCache] = useState<T[]>([]);
    const timer = useRef<NodeJS.Timeout | null>(null);
    const [count, setCount] = useState<number>(0);
    const [index, setIndex] = useState<number>(0);
    const [options, setOptions] = useState<SelectOption<T>[]>([]);
    const [mappedOptions, setMappedOptions] = useState<GroupedOptions<T>[]>([]);
    const [searchParams, setSearchParams] = useSearchParams();
    const [searchValue, setSearchValue] = useState<string | undefined>(undefined);
    const [isFetching, setIsFetching] = useState<boolean>(false);
    const fetchingMissingIdsRef = useRef<Set<string>>(new Set());

    // Fetch missing selected items that aren't in the cache
    useEffect(() => {
        if (!props.multiFetch || !props.value || props.value.length === 0) return;

        const cacheIds = new Set(cache.map((item) => item.id));
        const missingIds = props.value.filter((id) => !cacheIds.has(id) && !fetchingMissingIdsRef.current.has(id));

        if (missingIds.length === 0) return;

        // Track which IDs we're fetching to prevent duplicate requests (synchronous update via ref)
        missingIds.forEach((id) => fetchingMissingIdsRef.current.add(id));

        // Fetch all missing items in one batch
        props
            .multiFetch(missingIds)
            .then((results) => {
                setCache((prevCache) => {
                    const existingIds = new Set(prevCache.map((item) => item.id));
                    const newItems = results.filter((item) => !existingIds.has(item.id));
                    return [...prevCache, ...newItems];
                });
                setOptions((prevOptions) => {
                    const existingValues = new Set(prevOptions.map((o) => (isSingleOption(o) ? o.value : '')));
                    const newOptions = results.filter((item) => !existingValues.has(item.id)).map((item) => ({ value: item.id, label: props.itemLabel(item) }));
                    return [...prevOptions, ...newOptions];
                });
            })
            .finally(() => {
                missingIds.forEach((id) => fetchingMissingIdsRef.current.delete(id));
            });
    }, [props.value, cache, props.multiFetch]);

    const value: T[] = useMemo(() => {
        if (options.length === 0 && mappedOptions.length === 0) return [];
        const v = cache.filter((o) => props.value.includes(o.id));
        return v;
    }, [props.value, props.idValues, options, mappedOptions, cache]);

    const onSelect = (option: SingleOption<T>[] | null) => {
        if (options.length === 0 && mappedOptions.length === 0) return;
        if (option == null) {
            props.onChange && props.onChange([]);
            return;
        }
        props.onChange && props.onChange(cache.filter((c) => option.some((o) => o.value === c.id)));
    };

    useEffect(() => {
        const param = searchParams.getAll(props.useQueryParam?.param ?? '');
        if (!urlOverwrite && props.useQueryParam) {
            if (!param && !props.value) return;

            const valueIds = value.map((v) => v.id).sort();
            const paramIds = param ? param.sort() : [];
            const areEqual = valueIds.length === paramIds.length && valueIds.every((id, idx) => id === paramIds[idx]);

            if (areEqual) return;

            onQueryParamChange(valueIds, props.useQueryParam?.param ?? '', setSearchParams);
        }
    }, [value]);

    useEffect(() => {
        if (props.useUrlOverwrite && !props.disableFetch) {
            const param = searchParams.getAll(props.useUrlOverwrite.param);
            if (param !== undefined)
                props.useUrlOverwrite.fetch(param).then((result) => {
                    //setUrlOverwrite(result);
                    props.onChange && props.onChange(result);
                });
        }
    }, [props.useUrlOverwrite]);

    useEffect(() => {
        const queryParam = searchParams.getAll(props.useQueryParam?.param ?? '');
        if (props.useQueryParam && (value === null || value.length === 0) && queryParam !== null && !props.disableFetch) {
            props.useQueryParam.fetch(queryParam).then((result) => {
                props.onChange && props.onChange(result);
            });
        }
    }, [onQueryParamChange]);
    const previous = usePrevious({ params: props.queryParams, index: index, account: currentAccount, search: searchValue });
    useEffect(() => {
        if (!currentAccount) return;
        if (props.disableFetch) return;
        if (
            ((previous == undefined ||
                JSON.stringify(previous.params) != JSON.stringify(props.queryParams) ||
                previous.index != index ||
                previous.account != currentAccount ||
                previous.search !== searchValue) &&
                currentAccount) ||
            !props.value
        ) {
            setIsFetching(() => true);
            props
                .fetch({ index: index.toString(), size: pageSize, account_id: currentAccount, search: searchValue, ...props.queryParams })
                .then((result) => {
                    setCount(result.count);
                    if (index == 0) {
                        if (!props.mapOptions) {
                            setOptions(mapResultSetToOptions(result.results, props.itemLabel));
                            setCache(result.results);
                        } else {
                            setMappedOptions(mapResultSetToMappedOptions(result.results, props.mapOptions, props.itemLabel));
                            setCache(result.results);
                        }
                    } else {
                        if (!props.mapOptions) {
                            setOptions((options ?? []).concat(mapResultSetToOptions(result.results, props.itemLabel)));
                            setCache((cache ?? []).concat(result.results));
                        } else {
                            setOptions((mappedOptions ?? []).concat(mapResultSetToMappedOptions(result.results, props.mapOptions, props.itemLabel)));
                            setCache((cache ?? []).concat(result.results));
                        }
                    }
                })
                .finally(() => setIsFetching(() => false));
        }
    }, [index, props.queryParams, currentAccount, locale]);

    useEffect(() => {
        if (props.mapOptions) {
            mapResultSetToMappedOptions(cache, props.mapOptions, props.itemLabel);
        } else {
            setOptions(mapResultSetToOptions(cache, props.itemLabel));
        }
    }, [props.itemLabel, locale]);

    const search = (value: string) => {
        if (props.disableFetch) return;
        if (timer.current) {
            clearTimeout(timer.current);
        }
        timer.current = setTimeout(() => {
            props.fetch({ search: value, index: '0', size: pageSize, account_id: currentAccount, ...props.queryParams }).then((result) => {
                if (props.mapOptions) {
                    setMappedOptions(mapResultSetToMappedOptions(result.results, props.mapOptions, props.itemLabel));
                } else {
                    setOptions(mapResultSetToOptions(result.results, props.itemLabel));
                }
                setCache((prevCache) => {
                    const existingIds = new Set(prevCache.map((item) => item.id));
                    const newItems = result.results.filter((item) => !existingIds.has(item.id));
                    return [...prevCache, ...newItems];
                });
                setIndex(0);
                setCount(result.count);
                setSearchValue(value);
            });
        }, 500);
    };

    const scrollBottomVariableRef = useRef({ count, pageSize, index, searchValue });
    useEffect(() => {
        scrollBottomVariableRef.current = { count, pageSize, index, searchValue };
    }, [count, pageSize, index, searchValue]);
    const onScrollToBottom = () => {
        if (isFetching) return;
        const { count, pageSize, index } = scrollBottomVariableRef.current;
        if (count / parseInt(pageSize) > 1 && parseInt(pageSize) * index < count) {
            setIndex(index + 1);
        }
    };

    const opt = useMemo(() => {
        let result: SelectOption<T>[];
        if (props.subset) {
            if (props.subset.length === 0) return [];
            if (props.mapOptions) {
                const grouped: { label: string; options: { value: string; label: string }[] }[] = [];
                mappedOptions.forEach((o) => {
                    const current: { label: string; options: { value: string; label: string }[] } = { label: o.label, options: [] };
                    o.options.filter((op) => props.subset!.includes(op.value)).forEach((op) => current.options.push(op));
                    if (current.options.length > 0) grouped.push(current);
                });
                result = grouped as SelectOption<T>[];
            } else {
                result = options.filter((o) => {
                    if (isSingleOption(o)) {
                        return props.subset!.includes(o.value);
                    }
                    return o.options.filter((op) => props.subset!.includes(op.value)).length > 0;
                });
            }
        } else {
            result = props.mapOptions ? mappedOptions : options;
        }

        return result;
    }, [props.mapOptions, props.subset, options, mappedOptions]);

    return {
        options: opt,
        value,
        onScrollToBottom,
        search,
        onSelect,
        overwriteDisabled:
            urlOverwrite !== null || (!props.hideOnOneOption && options.length === 1 && !props.isClearable && props.required) || props.disableFetch,
        hide: props.hidden === true || (props.hideOnOneOption === true && options.length <= 1 && !props.isClearable)
    };
};

export default useMultiObjectSelectInput;
